import { Close as CloseIcon } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Warehouse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";

interface InventoryUpdateModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (
    warehouseId: number,
    partId: number,
    quantity: number,
    partCondition?: string
  ) => Promise<void>;
}

const InventoryUpdateModal: React.FC<InventoryUpdateModalProps> = ({
  open,
  onClose,
  onUpdate,
}) => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(
    null
  );
  const [selectedPart, setSelectedPart] =
    useState<TransferOrderPartDetails | null>(null);
  const [quantity, setQuantity] = useState<string>("");
  const [partCondition, setPartCondition] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Warehouse pagination and search states
  const [warehousePage, setWarehousePage] = useState(0);
  const [warehouseSearchQuery, setWarehouseSearchQuery] = useState("");
  const [hasMoreWarehouses, setHasMoreWarehouses] = useState(true);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  const warehouseDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const rowsPerPage = 10;

  // Fetch warehouses with infinite scroll
  const fetchWarehouses = useCallback(
    async (pageNumber: number, search: string, append: boolean = false) => {
      if (isLoadingWarehouses && !append) return;

      setIsLoadingWarehouses(true);
      try {
        const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
          pageNumber,
          rowsPerPage,
          search
        );

        const newWarehouses = response.data.data;
        const hasMore = pageNumber < response.data.totalPages - 1;

        if (append) {
          setWarehouses((prev) => {
            const existingIds = new Set(prev.map((w) => w.id));
            const uniqueNewWarehouses = newWarehouses.filter(
              (w) => !existingIds.has(w.id)
            );
            return [...prev, ...uniqueNewWarehouses];
          });
        } else {
          setWarehouses(newWarehouses);
        }

        setHasMoreWarehouses(hasMore);
        setWarehousePage(pageNumber);
      } catch (error) {
        console.error("Error fetching warehouses", error);
      } finally {
        setIsLoadingWarehouses(false);
      }
    },
    [isLoadingWarehouses]
  );

  // Handle warehouse search with debouncing
  const handleWarehouseInputChange = useCallback(
    (event: any, value: string, reason: string) => {
      if (reason !== "input") return;

      if (warehouseDebounceRef.current) {
        clearTimeout(warehouseDebounceRef.current);
      }

      warehouseDebounceRef.current = setTimeout(() => {
        setWarehousePage(0);
        setHasMoreWarehouses(true);
        fetchWarehouses(0, value, false);
      }, 300);
    },
    [fetchWarehouses]
  );

  // Handle warehouse scroll for infinite loading
  const handleWarehouseScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const threshold = listboxNode.scrollHeight - 10;

    if (position >= threshold && hasMoreWarehouses && !isLoadingWarehouses) {
      const nextPage = warehousePage + 1;
      fetchWarehouses(nextPage, warehouseSearchQuery, true);
    }
  };

  // Fetch parts function for AppAutoCompleteWithSearchAPI
  function fetchPaginatedPartsBySearchTerm({
    currentPageNumber,
    currentSearchQueryTerm,
  }: {
    currentPageNumber: number;
    currentSearchQueryTerm: string;
  }): Promise<{
    options: OptionType<TransferOrderPartDetails>[];
    hasMore: boolean;
  }> {
    return AllPartApi.api
      .getPartList(
        currentPageNumber,
        rowsPerPage,
        currentSearchQueryTerm,
        undefined,
        undefined,
        undefined
      )
      .then(({ data: response }) => ({
        options: response.data.map((part) => ({
          value: part.id.toString(),
          label: part.description,
          data: part,
        })),
        hasMore: currentPageNumber < response.totalPages - 1,
      }))
      .catch(() => ({ options: [], hasMore: false }));
  }

  // Handle part selection
  const handlePartSelect = (
    event: any,
    value: OptionType<TransferOrderPartDetails> | null
  ) => {
    if (value && value.data) {
      setSelectedPart(value.data);
    } else {
      setSelectedPart(null);
    }
  };

  // Handle update
  const handleUpdate = async () => {
    if (
      !selectedWarehouse ||
      !selectedPart ||
      !quantity ||
      isNaN(Number(quantity))
    ) {
      setError("Please fill in all fields with valid values");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      await onUpdate(selectedWarehouse.id, selectedPart.id, Number(quantity), partCondition || undefined);
      setSuccess("Inventory updated successfully!");

      // Reset form
      setSelectedWarehouse(null);
      setSelectedPart(null);
      setQuantity("");
      setPartCondition("");

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      setError(error?.message || "Failed to update inventory");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedWarehouse(null);
    setSelectedPart(null);
    setQuantity("");
    setPartCondition("");
    setError("");
    setSuccess("");
    onClose();
  };

  // Initial warehouse fetch
  useEffect(() => {
    if (open) {
      fetchWarehouses(0, "", false);
    }
  }, [open]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (warehouseDebounceRef.current) {
        clearTimeout(warehouseDebounceRef.current);
      }
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          borderBottom: "1px solid #e5e7eb",
        }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1E1E1E" }}>
          Update Inventory
        </Typography>
        <Button onClick={handleClose} sx={{ minWidth: "auto", p: 0 }}>
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Error/Success Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Warehouse Selection */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: "#374151" }}>
              Select Warehouse *
            </Typography>
            <Autocomplete
              options={warehouses}
              getOptionLabel={(option) => option.name}
              value={selectedWarehouse}
              loading={isLoadingWarehouses}
              onInputChange={handleWarehouseInputChange}
              onChange={(_, newValue) => setSelectedWarehouse(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search warehouses..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingWarehouses ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e5e7eb",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                  }}
                />
              )}
              ListboxProps={{
                onScroll: handleWarehouseScroll,
                style: { maxHeight: 200, overflow: "auto" },
              }}
            />
          </Box>

          {/* Parts Selection */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, color: "#374151" }}>
                Select Part *
              </Typography>
              <AppAutoCompleteWithSearchAPI
                onChange={handlePartSelect}
                initialPageNumber={0}
                disabled={!selectedWarehouse}
                value={
                  selectedPart
                    ? {
                        value: selectedPart.id.toString(),
                        label: selectedPart.description,
                        data: selectedPart,
                      }
                    : null
                }
                getData={fetchPaginatedPartsBySearchTerm}
                getOptionLabel={(option: OptionType<TransferOrderPartDetails>) =>
                  option.data?.description || ""
                }
                filterOptions={(options) => options}
                placeholder="Search parts..."
              />
            </Box>
            <Box sx={{ minWidth: 150 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, color: "#374151" }}>
                Type
              </Typography>
              <select
                value={partCondition}
                onChange={(e) => setPartCondition(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8.5px 12px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                  fontSize: "14px",
                  outline: "none",
                  backgroundColor: "white",
                  height: "40px",
                }}>
                <option value="">ALL</option>
                <option value="WORKING">Working</option>
                <option value="NOT_WORKING">Not Working</option>
              </select>
            </Box>
          </Box>

          {/* Quantity Input */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: "#374151" }}>
              Quantity *
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              type="number"
              value={quantity}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                  setQuantity(val);
                }
              }}
              placeholder="Enter quantity"
              disabled={!selectedPart}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#e5e7eb",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6",
                  },
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderColor: "#6b7280",
            color: "#6b7280",
            "&:hover": {
              borderColor: "#4b5563",
              backgroundColor: "rgba(107, 114, 128, 0.04)",
            },
          }}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={
            !selectedWarehouse || !selectedPart || !quantity || isLoading
          }
          sx={{
            textTransform: "none",
            bgcolor: "#171F21",
            "&:hover": { bgcolor: "#374151" },
            "&:disabled": { bgcolor: "#d1d5db" },
          }}>
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Update"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryUpdateModal;
